import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Users, TrendingUp, Award, CheckCircle, ArrowRight } from 'lucide-react';
export function EdoCase() {
  return <div className="min-h-screen bg-white pt-20" data-id="element-412">
      {/* Hero Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white" data-id="element-413">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-414">
          <div className="max-w-4xl" data-id="element-415">
            <div className="inline-block px-4 py-2 bg-blue-500/20 border border-blue-400/30 rounded-full mb-6" data-id="element-416">
              <span className="text-sm font-bold text-blue-300" data-id="element-417">
                Featured Case Study
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight" data-id="element-418">
              Edo State Government Digital Transformation
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed" data-id="element-419">
              A landmark project that revolutionized government operations and
              citizen engagement through comprehensive digital infrastructure
              across all Ministries, Departments, and Agencies.
            </p>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-12 sm:py-16 bg-slate-50" data-id="element-420">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-421">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8" data-id="element-422">
            <MetricCard value="78" label="MDA Websites Built" icon={Globe} data-id="element-423" />
            <MetricCard value="20" label="Portals Developed" icon={Users} data-id="element-424" />
            <MetricCard value="100%" label="Digital Coverage" icon={TrendingUp} data-id="element-425" />
            <MetricCard value="1M+" label="Citizens Reached" icon={Award} data-id="element-426" />
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-12 sm:py-20" data-id="element-427">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-428">
          <div className="grid lg:grid-cols-2 gap-12 sm:gap-16" data-id="element-429">
            <div data-id="element-430">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-431">
                Project Overview
              </h2>
              <div className="space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed" data-id="element-432">
                <p data-id="element-433">
                  Manuel Technologies was selected to lead the complete digital
                  transformation of Edo State Government, one of Nigeria's most
                  ambitious e-governance initiatives.
                </p>
                <p data-id="element-434">
                  The project involved designing, developing, and deploying a
                  comprehensive digital infrastructure spanning all 78
                  Ministries, Departments, and Agencies (MDAs), plus 20
                  specialized portals for critical government services.
                </p>
                <p data-id="element-435">
                  This transformation established Edo State as a leader in
                  digital governance, improving transparency, efficiency, and
                  citizen engagement across all government operations.
                </p>
              </div>
            </div>

            <div className="space-y-6" data-id="element-436">
              <InfoBox title="Client" content="Edo State Government" data-id="element-437" />
              <InfoBox title="Duration" content="18 months (Planning to Full Deployment)" data-id="element-438" />
              <InfoBox title="Scope" content="78 MDA Websites, 20 Portals, Main State Website Redesign" data-id="element-439" />
              <InfoBox title="Technologies" content="Custom CMS, Cloud Infrastructure, Mobile-Responsive Design, Security Protocols" data-id="element-440" />
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-441">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-442">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-443">
            Project Showcase
          </h2>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8" data-id="element-444">
            {/* Image 1 - Services Portal */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500" data-id="element-445">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jYoirDEpryhFhNbY159dU4/Screenshot_2025-12-02_045928.jpg" alt="Edo State Services Portal" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-446" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-447" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-448">
                <h3 className="text-xl font-black mb-2" data-id="element-449">
                  Edo State Services Portal
                </h3>
                <p className="text-sm text-slate-300" data-id="element-450">
                  Seamless government services platform with AI assistant
                  support
                </p>
              </div>
            </div>

            {/* Image 2 - Education Admin Portal */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500" data-id="element-451">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xrJrEqAhZd4W2BtR2e93bb/Screenshot_2025-12-02_045140.jpg" alt="Ministry of Education Admin Portal" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-452" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-453" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-454">
                <h3 className="text-xl font-black mb-2" data-id="element-455">
                  Ministry of Education Admin Portal
                </h3>
                <p className="text-sm text-slate-300" data-id="element-456">
                  Comprehensive dashboard for education administration and
                  analytics
                </p>
              </div>
            </div>

            {/* Image 3 - Health Admin Portal */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500" data-id="element-457">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aswtHzYYVWb9fUw2WHVBgH/Screenshot_2025-12-02_045105.jpg" alt="Ministry of Health Admin Portal" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-458" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-459" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-460">
                <h3 className="text-xl font-black mb-2" data-id="element-461">
                  Ministry of Health Admin Portal
                </h3>
                <p className="text-sm text-slate-300" data-id="element-462">
                  Healthcare management system with real-time monitoring and
                  reporting
                </p>
              </div>
            </div>

            {/* Image 4 - Citizen Dashboard */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500" data-id="element-463">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gvLzKgPYUxZ51PzdzYFdfp/Screenshot_2025-12-02_044938.jpg" alt="Citizen E-Services Dashboard" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-464" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-465" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-466">
                <h3 className="text-xl font-black mb-2" data-id="element-467">
                  Citizen E-Services Dashboard
                </h3>
                <p className="text-sm text-slate-300" data-id="element-468">
                  Personalized dashboard for citizens to access government
                  services
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-12 sm:py-20" data-id="element-469">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-470">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-471">
            Challenges & Solutions
          </h2>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8" data-id="element-472">
            <ChallengeCard challenge="Inconsistent Digital Presence" solution="Developed a unified design system and content management platform ensuring brand consistency across all 78 MDA websites while allowing customization for specific departmental needs." data-id="element-473" />
            <ChallengeCard challenge="Limited Technical Capacity" solution="Provided comprehensive training programs for government staff, enabling them to manage and update their websites independently, reducing long-term maintenance costs." data-id="element-474" />
            <ChallengeCard challenge="Citizen Accessibility" solution="Implemented mobile-first, responsive designs with accessibility features, ensuring all citizens could access government services regardless of device or ability." data-id="element-475" />
            <ChallengeCard challenge="Data Security Concerns" solution="Deployed enterprise-grade security protocols, SSL certificates, regular backups, and compliance with international data protection standards." data-id="element-476" />
          </div>
        </div>
      </section>

      {/* Key Deliverables */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-477">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-478">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-479">
            Key Deliverables
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-480">
            <DeliverableCard title="78 MDA Websites" items={['Custom designs for each ministry', 'Content management systems', 'News and announcements sections', 'Document repositories', 'Contact and feedback forms']} data-id="element-481" />
            <DeliverableCard title="20 Specialized Portals" items={['Citizen service portals', 'Business registration systems', 'Tax payment platforms', 'Healthcare management', 'Education administration']} data-id="element-482" />
            <DeliverableCard title="Main State Website" items={['Complete redesign and modernization', "Governor's office integration", 'State news and media center', 'Investment opportunities showcase', 'Tourism and culture promotion']} data-id="element-483" />
          </div>
        </div>
      </section>

      {/* Impact & Results */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-blue-900 to-slate-900 text-white" data-id="element-484">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-485">
          <h2 className="text-3xl sm:text-4xl font-black mb-8 sm:mb-12 text-center" data-id="element-486">
            Impact & Results
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-487">
            <ImpactCard title="Improved Governance" description="Enhanced transparency and accountability through accessible, up-to-date information on all government activities and initiatives." data-id="element-488" />
            <ImpactCard title="Better Citizen Engagement" description="Increased citizen participation through online feedback systems, service requests, and direct communication channels." data-id="element-489" />
            <ImpactCard title="Service Automation" description="Digitized key government services, reducing processing times from weeks to hours and eliminating physical queues." data-id="element-490" />
            <ImpactCard title="Cost Efficiency" description="Reduced operational costs through automated workflows, digital document management, and streamlined processes." data-id="element-491" />
            <ImpactCard title="Economic Growth" description="Attracted investment through improved digital infrastructure and transparent business registration processes." data-id="element-492" />
            <ImpactCard title="National Recognition" description="Edo State recognized as a leader in e-governance, setting standards for other Nigerian states to follow." data-id="element-493" />
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-494">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-495">
          <div className="text-5xl sm:text-6xl mb-6" data-id="element-496">💬</div>
          <blockquote className="text-xl sm:text-2xl font-semibold text-slate-800 mb-6 leading-relaxed" data-id="element-497">
            "Manuel Technologies transformed our vision of a digital Edo State
            into reality. Their expertise, professionalism, and commitment to
            excellence exceeded our expectations."
          </blockquote>
          <div className="font-black text-slate-900" data-id="element-498">Edo State Government</div>
          <div className="text-slate-600" data-id="element-499">
            Digital Transformation Initiative
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20" data-id="element-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-501">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-502">
            Ready for Your Digital Transformation?
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 mb-10" data-id="element-503">
            Let's discuss how we can help modernize your organization's digital
            infrastructure
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-black text-base sm:text-lg transition-all duration-300 hover:scale-105" data-id="element-504">
            Start Your Project
            <ArrowRight className="w-5 h-5" data-id="element-505" />
          </Link>
        </div>
      </section>
    </div>;
}
function MetricCard({
  value,
  label,
  icon: Icon
}: {
  value: string;
  label: string;
  icon: any;
}) {
  return <div className="text-center" data-id="element-506">
      <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-2xl mb-4" data-id="element-507">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" data-id="element-508" />
      </div>
      <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" data-id="element-509">
        {value}
      </div>
      <div className="text-xs sm:text-sm font-semibold text-slate-600" data-id="element-510">
        {label}
      </div>
    </div>;
}
function InfoBox({
  title,
  content
}: {
  title: string;
  content: string;
}) {
  return <div className="p-4 sm:p-6 bg-slate-50 rounded-xl border-l-4 border-blue-600" data-id="element-511">
      <div className="font-black text-slate-900 mb-2 text-sm sm:text-base" data-id="element-512">
        {title}
      </div>
      <div className="text-slate-700 text-sm sm:text-base" data-id="element-513">{content}</div>
    </div>;
}
function ChallengeCard({
  challenge,
  solution
}: {
  challenge: string;
  solution: string;
}) {
  return <div className="p-6 sm:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-514">
      <div className="text-red-600 font-black text-xs sm:text-sm mb-2" data-id="element-515">
        CHALLENGE
      </div>
      <h3 className="text-lg sm:text-xl font-black text-slate-900 mb-4" data-id="element-516">
        {challenge}
      </h3>
      <div className="text-green-600 font-black text-xs sm:text-sm mb-2" data-id="element-517">
        SOLUTION
      </div>
      <p className="text-slate-700 leading-relaxed text-sm sm:text-base" data-id="element-518">
        {solution}
      </p>
    </div>;
}
function DeliverableCard({
  title,
  items
}: {
  title: string;
  items: string[];
}) {
  return <div className="p-6 sm:p-8 bg-white rounded-2xl shadow-lg" data-id="element-519">
      <h3 className="text-xl sm:text-2xl font-black text-slate-900 mb-6" data-id="element-520">
        {title}
      </h3>
      <ul className="space-y-3" data-id="element-521">
        {items.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-522">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-523" />
            <span className="text-slate-700 text-sm sm:text-base" data-id="element-524">{item}</span>
          </li>)}
      </ul>
    </div>;
}
function ImpactCard({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-300" data-id="element-525">
      <h3 className="text-lg sm:text-xl font-black mb-3" data-id="element-526">{title}</h3>
      <p className="text-slate-300 leading-relaxed text-sm sm:text-base" data-id="element-527">
        {description}
      </p>
    </div>;
}